import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle, Utensils, ArrowRight, Mail, Lock, Phone, KeyRound, Eye, EyeOff } from 'lucide-react';
import { customerAuth } from '../../services/customerService';

/**
 * CustomerAccountLogin
 *
 * Account-based customer login that gates the menu. Two modes:
 *   - Password: email + password (the Customer model's login).
 *   - OTP (mock): phone → request code → verify. The backend returns the code
 *     in the response so it can be shown here without a real SMS gateway.
 *
 * On success the customer auth token + profile are stored and the guest is
 * sent to `?next=` (the menu URL they were trying to reach) or the menu.
 */
export default function CustomerAccountLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const next = params.get('next') || '/customer/menu';

  // Personalise the welcome: the `next` URL carries the table-QR token/slug —
  // resolve it (public endpoint) into the restaurant name + table code.
  const [place, setPlace] = useState(null); // {restaurant_name, table_code} | null
  useEffect(() => {
    try {
      const nextParams = new URLSearchParams((next.split('?')[1] || ''));
      const qrToken = nextParams.get('token') || nextParams.get('qr');
      const API_URL = process.env.REACT_APP_API_URL || '/api';
      if (qrToken) {
        // Table-QR flow: the token resolves to the restaurant + table.
        fetch(`${API_URL}/users/customer/table-status/?token=${encodeURIComponent(qrToken)}`)
          .then((r) => r.json())
          .then((d) => {
            if (d && d.success && d.data) {
              setPlace({
                restaurant_name: d.data.restaurant_name || '',
                table_code: d.data.table_code || '',
              });
            }
          })
          .catch(() => {});
        return;
      }
      // Reservation / menu-QR flow: only a restaurant id — greet with the
      // primary branch's name (public endpoint).
      const restaurantId = nextParams.get('restaurant');
      if (restaurantId) {
        fetch(`${API_URL}/customers/restaurants/${restaurantId}/locations/`)
          .then((r) => r.json())
          .then((d) => {
            const first = d && d.success && (d.data || [])[0];
            if (first?.name) setPlace({ restaurant_name: first.name, table_code: '' });
          })
          .catch(() => {});
      }
    } catch { /* generic welcome */ }
  }, [next]);

  const [mode, setMode] = useState('password'); // 'password' | 'otp'
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Password mode
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // OTP mode — Mauritius mobile: +230 shown as country code, the user types
  // the full 8-digit number (state holds those 8 digits).
  const [phone, setPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [salutation, setSalutation] = useState(''); // '' | MR | MRS | MS
  // Set when the phone already has an account: {name} — greet the guest
  // instead of asking for their name again. null = new number.
  const [knownCustomer, setKnownCustomer] = useState(null);

  const finish = (data) => {
    customerAuth.save(data.token, data.customer);
    navigate(next, { replace: true });
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Enter your email and password.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const data = await customerAuth.login(email, password);
      finish(data);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (phone.length !== 8) {
      setError('Enter your 8-digit mobile number.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const data = await customerAuth.requestOtp(phone);
      setKnownCustomer(data?.known_customer ? { name: data.known_name || '' } : null);
      setOtpSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!knownCustomer && !name.trim()) {
      setError('Please enter your name to continue.');
      return;
    }
    if (!code) {
      setError('Enter the 6-digit code.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const data = await customerAuth.verifyOtp(phone, code, name, salutation);
      finish(data);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  /* text-gray-900 is explicit: with no color of its own the input INHERITS,
     and what it inherits here washed the typed number out to placeholder
     grey — the field looked empty while being filled in. */
  const inputCls =
    'w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 caret-amber-600 focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 outline-none transition';

  /* text-gray-900 pinned at the root: this page is hard-coded light, but it
     used to INHERIT the app theme's foreground — pale beige in dark mode —
     which washed every unstyled control out to near-invisible. */
  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-amber-50 via-white to-orange-50 text-gray-900 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-amber-100 shadow-sm mb-4">
            <Utensils className="h-7 w-7 text-amber-700" />
          </div>
          {place?.restaurant_name ? (
            <>
              <h1 className="text-3xl font-semibold tracking-tight text-gray-900">
                Welcome to {place.restaurant_name}
              </h1>
              <p className="mt-2 text-sm text-gray-500">
                Sign in to confirm your table{place.table_code ? ` (Table ${place.table_code})` : ''} and start ordering.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-semibold tracking-tight text-gray-900">Sign in to continue</h1>
              <p className="mt-2 text-sm text-gray-500">Log in to view the menu and order.</p>
            </>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8">
          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-2 mb-6 p-1 bg-gray-100 rounded-lg">
            <button
              onClick={() => { setMode('password'); setError(''); }}
              className={`py-2 rounded-md text-sm font-medium transition ${
                mode === 'password' ? 'bg-white shadow text-amber-700' : 'text-gray-500'
              }`}
            >
              Password
            </button>
            <button
              onClick={() => { setMode('otp'); setError(''); }}
              className={`py-2 rounded-md text-sm font-medium transition ${
                mode === 'otp' ? 'bg-white shadow text-amber-700' : 'text-gray-500'
              }`}
            >
              Login via OTP
            </button>
          </div>

          {mode === 'otp' && (
            <p className="-mt-3 mb-5 text-xs text-gray-500">
              New here? Sign in with a one-time code and we'll create your account.
            </p>
          )}

          {error && (
            <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 mb-5">
              <AlertCircle className="h-5 w-5 mt-0.5 text-red-500 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {mode === 'password' ? (
            <form onSubmit={handlePasswordLogin} className="space-y-5">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input type="email" autoComplete="email" placeholder="Email address"
                  value={email} onChange={(e) => setEmail(e.target.value)} disabled={submitting} className={inputCls} />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input type={showPassword ? 'text' : 'password'} autoComplete="current-password" placeholder="Password"
                  value={password} onChange={(e) => setPassword(e.target.value)} disabled={submitting}
                  className={`${inputCls} pr-11`} />
                <button type="button" onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <button type="submit" disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-medium tracking-wide shadow-sm transition">
                {submitting ? 'Signing in…' : 'Sign in'}
                {!submitting && <ArrowRight className="h-4 w-4" />}
              </button>
            </form>
          ) : (
            <div className="space-y-5">
              {!otpSent ? (
                <form onSubmit={handleRequestOtp} className="space-y-5">
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <span className="absolute left-9 top-1/2 -translate-y-1/2 text-gray-700 font-medium select-none">+230</span>
                    <input type="tel" inputMode="numeric" placeholder="XXXX XXXX" maxLength={8}
                      value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 8))} disabled={submitting}
                      className={`${inputCls} pl-[5.5rem]`} />
                  </div>
                  <button type="submit" disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-medium tracking-wide shadow-sm transition">
                    {submitting ? 'Sending…' : 'Send OTP'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-5">
                  {/* The number the code was sent to — fixed; "Use a different
                      number" below is the only way back. */}
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input type="text" value={`+230 ${phone}`} readOnly disabled
                      aria-label="Phone number the code was sent to"
                      className={`${inputCls} bg-gray-50 text-gray-500 cursor-not-allowed`} />
                  </div>
                  {knownCustomer ? (
                    /* Existing account — greet by name, no name field. */
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                      Welcome back{knownCustomer.name ? `, ${knownCustomer.name}` : ''}! Enter the
                      code we sent to sign in.
                    </div>
                  ) : (
                  /* New number — name is mandatory, with an optional Mr/Mrs/Ms salutation. */
                  <div className="flex gap-2">
                    <select
                      value={salutation}
                      onChange={(e) => setSalutation(e.target.value)}
                      disabled={submitting}
                      aria-label="Salutation"
                      className="w-24 shrink-0 py-3 px-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 outline-none transition"
                    >
                      {/* Explicit color on the options too — the popup list inherits, and inherited here meant near-invisible. */}
                      <option value="" className="text-gray-900">None</option>
                      <option value="MR" className="text-gray-900">Mr</option>
                      <option value="MRS" className="text-gray-900">Mrs</option>
                      <option value="MS" className="text-gray-900">Ms</option>
                    </select>
                    <div className="relative flex-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input type="text" placeholder="Your name *" required
                        value={name} onChange={(e) => setName(e.target.value)} disabled={submitting} className={inputCls} />
                    </div>
                  </div>
                  )}
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input type="text" inputMode="numeric" placeholder="6-digit code" maxLength={6}
                      value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} disabled={submitting}
                      className={`${inputCls} tracking-[0.4em] font-semibold`} />
                  </div>
                  <button type="submit" disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-medium tracking-wide shadow-sm transition">
                    {submitting ? 'Verifying…' : 'Verify & continue'}
                    {!submitting && <ArrowRight className="h-4 w-4" />}
                  </button>
                  <button type="button" onClick={() => { setOtpSent(false); setCode(''); setError(''); setKnownCustomer(null); }}
                    className="w-full text-center text-xs text-gray-500 hover:text-gray-700">
                    Use a different number
                  </button>
                </form>
              )}
            </div>
          )}
        </div>

        {/* Skip — view the menu without an account; ordering asks to sign in. */}
        <button
          type="button"
          onClick={() =>
            navigate(`${next}${next.includes('?') ? '&' : '?'}browse=1`, { replace: true })
          }
          className="mt-4 w-full text-center text-sm font-medium text-amber-700 hover:text-amber-800"
        >
          {next.includes('/customer/reserve')
            ? 'Skip for now — just have a look'
            : 'Skip for now — just browse the menu'}
        </button>

        <p className="mt-6 text-center text-xs text-gray-400">Powered by RestoBoxx · Secure customer entry</p>
      </div>
    </div>
  );
}
